import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, Storefront, Truck, HardHat } from "@phosphor-icons/react";

export default function DosDonts() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20 lg:pt-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate("/support")}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft size={20} weight="bold" />
          <span className="font-medium">Back to Support</span>
        </button>

        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-black text-[#0F172A] mb-4">Do&apos;s & Don&apos;ts Guidelines</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Practical guidance for Sellers, Delivery Partners and Customers to keep every Loado delivery safe,
            compliant and efficient.
          </p>
        </div>

        <div className="space-y-12">
          <section className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <Storefront size={24} weight="duotone" className="text-[#FF5500]" />
              <h2 className="font-bold text-xl text-[#0F172A]">For Loado Sellers (Material Stores)</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                  <span>DO&apos;S</span> <span>✅</span>
                </h3>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Inspect Before Loading:</strong> Check for moisture in cement or rust on steel before handing it to the driver.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Package Securely:</strong> Ensure loose materials like sand are leveled and bricks are stacked safely.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Document the Load:</strong> Take a photo of the material on the vehicle before it leaves your yard for your records.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Accurate Manifest:</strong> Ensure the weight and quantity match the customer's digital invoice exactly.</span>
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                  <span>DON&apos;TS</span> <span>❌</span>
                </h3>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Don't Delay the Driver:</strong> Keep the material ready for pickup. Delays over each vehicle's permitted waiting time affect the Seller's payout earnings.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Don't Send Substandard Goods:</strong> If the site rejects material for quality, you are liable for the full fleet cost.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Don't Over-Promise:</strong> Only mark items as "Available" if they can be dispatched within the one-day delivery window.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Don't Bypass the App:</strong> Never arrange private deals with Loado drivers; these are not insured or tracked.</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <Truck size={24} weight="duotone" className="text-[#0055FF]" />
              <h2 className="font-bold text-xl text-[#0F172A]">For Loado Delivery Partners (Drivers)</h2>
            </div>
            <p className="text-slate-600 text-sm mb-4 italic">
              You are the face of Loado on the construction site. Follow these to maximize your ratings and earnings.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                  <span>DO&apos;S</span> <span>✅</span>
                </h3>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Verify the Load:</strong> Check that the material matches the app description before you start the engine.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Use Tarpaulins:</strong> Always cover cement, putty, or sand to protect against rain, wind, or dust during transit.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Collect Fleet Charges:</strong> For "Pay After Delivery" orders, ensure you receive the fleet fee before leaving the site.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Documents & Invoices:</strong> Collect all necessary transportation documents before leaving the Pickup point.</span>
                  </li>
                </ul>
              </div>
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                  <span>DON&apos;TS</span> <span>❌</span>
                </h3>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Don't Overload:</strong> Never carry more than your vehicle's legal RC capacity. Safety is the priority.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Don't Unload Without Approval:</strong> If a customer mentions a "Quality Issue," wait for them to take photos before unloading.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Don't Change Routes:</strong> Stick to the app's GPS tracking to ensure the customer sees your real-time progress.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Don't Demand Extra Cash:</strong> Never ask for "extra tips." All official charges are handled by the app.</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <HardHat size={24} weight="duotone" className="text-[#FF5500]" />
              <h2 className="font-bold text-xl text-[#0F172A]">For Loado Customers (Builders & Enterprises)</h2>
            </div>
            <p className="text-slate-600 text-sm mb-4 italic">
              Whether you are booking a bike for a small tool or 10 tons of steel, follow these rules for a smooth delivery.
            </p>
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
              <h3 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                <span>The Customer DO&apos;S</span> <span>✅</span>
              </h3>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Inspect Before Unloading:</strong> For material purchases, you must check quality, quantity, and grade while the materials are still on the vehicle. This is your only window for a full refund.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Give Transportation Documents:</strong> Invoice/Delivery challan/E-way Bill like transportation supporting documents give to delivery partner for smooth transportation of your Material.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Clear the Drop-Off Zone:</strong> Ensure your construction site has enough space for the specific vehicle you booked (e.g., ensure a 10-wheeler can actually turn into your gate).</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Be Ready for the Driver:</strong> Have your unloading labor or crane ready. Drivers are allotted a 30-minute free waiting window; delays beyond this will incur "Waiting Charges."</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Pay the Fleet Charge:</strong> For logistics-only bookings, remember that the Fleet/Logistics fee is payable after delivery. Ensure you have the funds ready to pay the partner directly or via the app.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Provide Correct Contacts:</strong> Always provide the phone number of the Site Supervisor who will actually receive the goods, not just the office number.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Report Issues with Proof:</strong> If there is a quality dispute, take clear, timestamped photos of the material on the truck and email support@loado.in immediately.</span>
                </li>
              </ul>
            </div>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                <span>The Customer DON&apos;TS</span> <span>❌</span>
              </h3>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Don't Reject After Unloading:</strong> Once the material (sand, cement, bricks) is unloaded onto your site, it is considered "Accepted." No refunds or returns are possible after the truck is empty.</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Don't Change Destinations:</strong> Drivers follow the GPS route provided in the app. Do not ask a driver to "divert" to a different site once they are on the way without updating it in the app.</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Don't Overload the Vehicle:</strong> If you booked a Pickup but try to load it with 3 tons of steel, the driver has the right to cancel the trip, and you will be liable for the Cancellation Fee.</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle size={16} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Don't Negotiate Outside Loado:</strong> Never pay a driver or seller "extra cash" or "side payments." Loado cannot protect your money or guarantee service if the transaction happens outside our platform.</span>
                </li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
